import React, { useCallback, useMemo, useState } from 'react';
import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  Box,
  ButtonBase,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  ChatTagGlossary,
  FAMILY_LABELS,
  HardwareFamily,
  PickerOption,
  TagStatus,
  familyCaption,
  isFilteringFamily,
  pickerOptions,
  searchTags,
  tagLabel,
  tagStatus,
} from './tagGlossary';

interface Props {
  /** Null while the glossary loads, or when it could not be read. */
  glossary: ChatTagGlossary | null;
  glossaryError?: string | null;
  /** The session's tags as ant last returned them. */
  selected: string[];
  /** The session's rental family; undefined while ant has not said. */
  family: HardwareFamily | null | undefined;
  /** Rejects, or resolves once ant refused, to drop the pending chip; the caller says why. */
  onAdd: (id: string) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
  disabled?: boolean;
}

/** One row of the Autocomplete: an offered glossary tag, or a selected id the glossary does not offer. */
interface PickerEntry {
  slug: string;
  label: string;
  group: string;
  definition: string | null;
  families: HardwareFamily[];
  status: TagStatus;
  /** Present when the tag can be added from the list. */
  option: PickerOption | null;
}

const STATUS_TOOLTIP: Record<Exclude<TagStatus, 'active'>, string> = {
  retired: 'Retired tag: still counted, cannot be added again',
  legacy: 'Older tag, not in the glossary',
  unknown: 'Not in the glossary',
};

/**
 * Ranked options regrouped so each category's rows sit together (MUI's
 * groupBy needs that), categories in the order their best hit came in.
 */
function groupWhole(ranked: PickerEntry[]): PickerEntry[] {
  const groups = new Map<string, PickerEntry[]>();
  ranked.forEach((entry) => {
    const rows = groups.get(entry.group);
    if (rows) rows.push(entry);
    else groups.set(entry.group, [entry]);
  });
  return Array.from(groups.values()).flat();
}

/**
 * The tags on a session and the glossary tags an agent can add, grouped by
 * category, narrowed to the session's rental family (kayak, bike, lawn
 * game) unless "Show all tags", a switch that is always there and sits on,
 * locked, whenever nothing narrows the list. Every chip can be removed: a retired tag,
 * an old string, whatever ant returned. One `onAdd` or `onRemove` per
 * click, the chip shown pending until the caller's promise settles.
 */
export function TagPicker({
  glossary,
  glossaryError = null,
  selected,
  family,
  onAdd,
  onRemove,
  disabled = false,
}: Props) {
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [pendingAdds, setPendingAdds] = useState<string[]>([]);
  const [pendingRemoves, setPendingRemoves] = useState<string[]>([]);

  const filtering = isFilteringFamily(family);

  const offered = useMemo<PickerEntry[]>(
    () =>
      glossary
        ? pickerOptions(glossary, family, showAll).map((option) => ({
            slug: option.tag.slug,
            label: option.tag.label,
            group: option.category.name,
            definition: option.tag.definition,
            families: option.tag.appliesTo,
            status: 'active',
            option,
          }))
        : [],
    [glossary, family, showAll]
  );

  /** What the chips show: the session's tags plus the adds in flight, minus the removes. */
  const shown = useMemo(() => {
    const ids = [...selected, ...pendingAdds.filter((id) => !selected.includes(id))];
    return ids.filter((id) => !pendingRemoves.includes(id));
  }, [selected, pendingAdds, pendingRemoves]);

  const entryFor = useCallback(
    (id: string): PickerEntry =>
      offered.find((entry) => entry.slug === id) ?? {
        slug: id,
        label: tagLabel(glossary, id),
        group: '',
        definition: null,
        families: [],
        status: tagStatus(glossary, id),
        option: null,
      },
    [offered, glossary]
  );

  const value = useMemo(() => shown.map(entryFor), [shown, entryFor]);

  // Every selected entry is among the options too, so MUI never sees a
  // value it cannot match; filterOptions keeps them out of the list.
  const options = useMemo(
    () => [...offered, ...value.filter((entry) => entry.option === null)],
    [offered, value]
  );

  const filterOptions = useCallback(
    (all: PickerEntry[], state: { inputValue: string }) => {
      const bySlug = new Map(all.map((entry) => [entry.slug, entry]));
      const ranked = searchTags(
        all.filter((entry) => entry.option !== null).map((entry) => entry.option!),
        state.inputValue
      );
      return groupWhole(
        ranked
          .map((option) => bySlug.get(option.tag.slug))
          .filter((entry): entry is PickerEntry => entry !== undefined)
      );
    },
    []
  );

  const add = async (id: string) => {
    setPendingAdds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    try {
      await onAdd(id);
    } catch (_) {
      // The caller has said why.
    } finally {
      setPendingAdds((prev) => prev.filter((other) => other !== id));
    }
  };

  const remove = async (id: string) => {
    setPendingRemoves((prev) => (prev.includes(id) ? prev : [...prev, id]));
    try {
      await onRemove(id);
    } catch (_) {
      // The caller has said why.
    } finally {
      setPendingRemoves((prev) => prev.filter((other) => other !== id));
    }
  };

  const toggleGroup = (group: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });

  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    const count = React.Children.count(params.children);
    const isOpen = query.trim() !== '' || expanded.has(params.group);
    return (
      <li key={params.key}>
        <ButtonBase
          onClick={() => toggleGroup(params.group)}
          aria-expanded={isOpen}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            px: 1.5,
            py: 0.75,
            gap: 0.5,
            typography: 'caption',
            fontWeight: 600,
            color: 'text.secondary',
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          {isOpen ? (
            <ExpandMoreIcon fontSize="inherit" />
          ) : (
            <ChevronRightIcon fontSize="inherit" />
          )}
          {`${params.group} (${count})`}
        </ButtonBase>
        {isOpen && <ul style={{ padding: 0 }}>{params.children}</ul>}
      </li>
    );
  };

  const caption = !glossary
    ? 'Loading the glossary…'
    : family === undefined
    ? 'Checking the rental type…'
    : familyCaption(family, showAll);

  return (
    <Stack gap={0.75} data-testid="tag-picker">
      <Autocomplete<PickerEntry, true, true, false>
        multiple
        disableClearable
        disableCloseOnSelect
        filterSelectedOptions
        autoHighlight
        size="small"
        disabled={disabled}
        options={options}
        value={value}
        inputValue={query}
        onInputChange={(_, next, reason) => {
          if (reason !== 'reset') setQuery(next);
        }}
        groupBy={(entry) => entry.group}
        getOptionLabel={(entry) => entry.label}
        isOptionEqualToValue={(a, b) => a.slug === b.slug}
        filterOptions={filterOptions}
        onHighlightChange={(_, entry) => {
          if (entry && !expanded.has(entry.group)) toggleGroup(entry.group);
        }}
        onChange={(_, __, reason, details) => {
          const entry = details?.option;
          if (!entry) return;
          if (reason === 'selectOption') add(entry.slug);
          else if (reason === 'removeOption') remove(entry.slug);
        }}
        noOptionsText={glossary ? 'No tag matches' : 'Loading the glossary…'}
        ListboxProps={{ style: { maxHeight: 360 } }}
        renderGroup={renderGroup}
        renderOption={(props, entry) => (
          <li {...props} key={entry.slug} data-testid="tag-option" data-slug={entry.slug}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">{entry.label}</Typography>
              {entry.definition && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  component="div"
                  sx={{ maxWidth: 420 }}
                >
                  {entry.definition}
                </Typography>
              )}
              {entry.families.length > 0 && (
                <Stack direction="row" gap={0.5} sx={{ mt: 0.25 }}>
                  {entry.families.map((f) => (
                    <Chip
                      key={f}
                      label={FAMILY_LABELS[f]}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: 11 }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </li>
        )}
        renderTags={(entries, getTagProps) =>
          entries.map((entry, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const pending =
              pendingAdds.includes(entry.slug) || pendingRemoves.includes(entry.slug);
            const chip = (
              <Chip
                {...tagProps}
                key={key}
                label={entry.label}
                size="small"
                color={entry.status === 'active' ? 'primary' : 'default'}
                variant={entry.status === 'active' ? 'filled' : 'outlined'}
                data-testid="tag-chip"
                data-status={entry.status}
                data-pending={pending ? 'true' : 'false'}
                sx={{
                  opacity: pending ? 0.6 : 1,
                  ...(entry.status === 'retired'
                    ? { textDecoration: 'line-through', borderStyle: 'dashed' }
                    : {}),
                }}
              />
            );
            return entry.status === 'active' ? (
              chip
            ) : (
              <Tooltip key={key} title={STATUS_TOOLTIP[entry.status]}>
                {chip}
              </Tooltip>
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add a tag"
            placeholder={value.length === 0 ? 'Search tags' : undefined}
            inputProps={{ ...params.inputProps, 'aria-label': 'Add a tag' }}
          />
        )}
      />
      {glossaryError ? (
        <Typography variant="caption" color="error">
          Could not load the glossary. {glossaryError}
        </Typography>
      ) : (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Switch
                size="small"
                checked={!filtering || showAll}
                disabled={disabled || !filtering}
                onChange={(e) => setShowAll(e.target.checked)}
                inputProps={{ 'aria-label': 'Show all tags' }}
              />
            }
            label={<Typography variant="caption">Show all tags</Typography>}
          />
        </Stack>
      )}
    </Stack>
  );
}
